/**
 * App Status Service
 * Shared service for checking RightPlace app status and device connections
 */

// Debounce tracking
let lastCheckTime = 0;
let pendingCheck = null;
const DEBOUNCE_INTERVAL = 3000; // 3 seconds

/**
 * Debug logging helper
 */
function debugLog(config, ...args) {
	if (config && config.debug) {
		console.log(...args);
	}
}

/**
 * Check RightPlace app status with debouncing
 * @param {Object} config - WordPress configuration object
 * @returns {Promise<Object>} App status object
 */
export async function checkAppStatus(config) {
	// Check if we're within the debounce window
	const now = Date.now();
	const timeSinceLastCheck = now - lastCheckTime;
	
	if (timeSinceLastCheck < DEBOUNCE_INTERVAL) {
		// debugLog(config, `[AppStatusService] Debouncing checkAppStatus (${timeSinceLastCheck}ms since last check, need ${DEBOUNCE_INTERVAL}ms)`);
		
		// If there's already a pending check, return that promise
		if (pendingCheck) {
			return pendingCheck;
		}
		
		// Wait for the remaining debounce time
		const remainingTime = DEBOUNCE_INTERVAL - timeSinceLastCheck;
		await new Promise(resolve => setTimeout(resolve, remainingTime));
	}
	
	// Update last check time
	lastCheckTime = Date.now();
	
	// Create a new check promise
	pendingCheck = performAppStatusCheck(config);
	
	try {
		const result = await pendingCheck;
		return result;
	} finally {
		pendingCheck = null;
	}
}

/**
 * Perform the actual app status check
 * @param {Object} config - WordPress configuration object
 * @returns {Promise<Object>} App status object
 */
async function performAppStatusCheck(config) {
    try {
        let serverAddress = '';
        
        // Try to get server address from desktop app first
        if (window.__rightplaceClient && typeof window.__rightplaceClient.getExternalHostUrl === 'function') {
            const hostUrlData = await window.__rightplaceClient.getExternalHostUrl();
            debugLog(config, '[AppStatusService] Desktop app data:', hostUrlData);
            
            if (hostUrlData.success && hostUrlData.externalServerAddress) {
                serverAddress = hostUrlData.externalServerAddress;
            }
        }
        
        // If no server address from desktop app, load from saved option
        if (!serverAddress) {
            // debugLog(config, '[AppStatusService] No server address from desktop app, loading saved address');
            serverAddress = await getSavedServerAddress(config);
        }
        
        // If still no server address, use default
        if (!serverAddress) {
            //debugLog(config, '[AppStatusService] No saved address, using default:', config.defaultServerAddress);
            serverAddress = config.defaultServerAddress || 'http://localhost:8080';
        }
        
        debugLog(config, '[AppStatusService] Checking health at:', serverAddress);
        
        // Call the health endpoint to determine if server is running
        try {
            const healthResponse = await fetch(`${serverAddress}/v0/health`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                signal: AbortSignal.timeout(5000) // 5 second timeout
            });
            
            const healthData = await healthResponse.json();
            
            if (healthData.ok === true) {
                // Server is running and healthy
                const devicePid = healthData.devicePid || '';
                
                // Set initial state
                const appStatus = {
                    success: true,
                    externalServerAddress: serverAddress,
                    externalServerRunning: true,
                    isChecking: true, // Keep checking while we verify device connection
                    devicePid: devicePid,
                    isDeviceConnected: false,
                    isCheckingDevice: false,
                    hasChecked: false
                };
                
                // Check if device is connected to current user
                if (devicePid) {
                    const deviceConnected = await checkDeviceConnection(devicePid, config);
                    appStatus.isDeviceConnected = deviceConnected;
                }
                
                // Now mark as fully checked
                appStatus.isChecking = false;
                appStatus.hasChecked = true;
                
                debugLog(config, '[AppStatusService] Desktop app is connected and running');
                return appStatus;
            } else {
                // Server responded but not healthy
                return {
                    success: false,
                    externalServerAddress: serverAddress,
                    externalServerRunning: false,
                    isChecking: false,
                    hasChecked: true
                };
            }
        } catch (fetchError) {
            if (config && config.debug) {
                console.error('[AppStatusService] Health check failed:', fetchError);
            }
            // Server is not reachable
            return {
                success: false,
                externalServerAddress: serverAddress,
                externalServerRunning: false,
                isChecking: false,
                hasChecked: true
            };
        }
    } catch (error) {
        if (config && config.debug) {
            console.error('[AppStatusService] Error checking app status:', error);
        }
        return {
            success: false,
            externalServerAddress: '',
            externalServerRunning: false,
            isChecking: false,
            hasChecked: true
        };
    }
}

/**
 * Get saved server address from WordPress options
 * @param {Object} config - WordPress configuration object
 * @returns {Promise<string>} Server address
 */
export async function getSavedServerAddress(config) {
    try {
        const response = await fetch(config.wpJsonUrl + 'rightplace/v1/server-address', {
            headers: {
                'X-WP-Nonce': config.nonce
            }
        });
        const data = await response.json();
        return data.address || null;
    } catch (error) {
        if (config && config.debug) {
            console.error('[AppStatusService] Error loading saved address:', error);
        }
        return null;
    }
}

/**
 * Check if device is connected to current user
 * @param {string} devicePid - Device public key
 * @param {Object} config - WordPress configuration object
 * @returns {Promise<boolean>} Whether device is connected
 */
export async function checkDeviceConnection(devicePid, config) {
    try {
        const response = await fetch(`${config.wpJsonUrl}rightplace/v1/check-device-connection`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': config.nonce
            },
            body: JSON.stringify({ device_pid: devicePid })
        });

        if (!response.ok) {
            return false;
        }

        const data = await response.json();
        return data.is_connected || false;
    } catch (error) {
        if (config && config.debug) {
            console.error('[AppStatusService] Error checking device connection:', error);
        }
        return false;
    }
}

/**
 * Update server address via API
 * @param {string} address - Server address to save
 * @param {Object} config - WordPress configuration object
 * @returns {Promise<void>}
 */
export async function updateServerAddress(address, config) {
    try {
        const response = await fetch(config.wpJsonUrl + 'rightplace/v1/server-address', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': config.nonce
            },
            body: JSON.stringify({ address })
        });

        if (!response.ok) {
            throw new Error('Failed to update server address');
        }

        const data = await response.json();
        debugLog(config, '[AppStatusService] Server address updated:', data);
    } catch (error) {
        if (config && config.debug) {
            console.error('[AppStatusService] Error updating server address:', error);
        }
        throw error;
    }
}

/**
 * Get external host URL from desktop app
 * @returns {Promise<Object>} Host URL data
 */
export async function getExternalHostUrl() {
    if (window.__rightplaceClient && typeof window.__rightplaceClient.getExternalHostUrl === 'function') {
        return await window.__rightplaceClient.getExternalHostUrl();
    }
    return { success: false };
}
